import React from 'react';
import ISColorPicker from 'containers/IntermediateShelfPanel/ISColorPicker';
import ISDimensionSelector from 'containers/IntermediateShelfPanel/ISDimensionSelector';
import DimensionCabinetSelector from 'containers/Ribbon/CabinetRibbon/DimensionCabinetSelector';
import sizeDepth from 'assets/images/size/depth.svg';
import {
  ISDEPTH,
  marksISDepth,
} from 'containers/IntermediateShelfPanel/constants';
import { StyledMaterialButton } from 'containers/IntermediateShelfPanel/index';
import { useTranslation } from 'react-i18next';
import {
  updateDepthIntermediateShelvesOnSelectionAction,
  updateHeightIntermediateShelvesOnSelectionAction,
} from 'containers/IntermediateShelfPanel/actions';
import { useDispatch, useSelector } from 'react-redux';
import { restoreCabinet } from 'containers/Configurator/actions/furniture';
import { createStructuredSelector } from 'reselect';
import { RootState } from 'containers/IntermediateShelfPanel/types';
import { makeSelectIsActiveCustomMode } from 'containers/Configurator/selectors/general';
import { makeSelectShelvesSelectedLastCabinet } from 'containers/Configurator/selectors/selection';
import { makeSelectLastHistoryCabinet } from 'containers/Configurator/selectors/history';
import {
  makeSelectAllowISHeight,
  makeSelectCommonColorIS,
  makeSelectCommonColorsIS,
  makeSelectDepthIS,
  makeSelectDepthMaxIS,
  makeSelectDepthValuesIS,
  makeSelectHeightIS,
  makeSelectIsSameDepthIntermediateShelvesInShelvesSelected,
} from 'containers/IntermediateShelfPanel/selectors';
import { makeSelectCabinetDepth } from 'containers/Configurator/selectors/furniture';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import useMedia from 'use-media';
import { Color } from 'lib/modelBuilder/entities/Color';

// import styled from 'styled-components/macro';

interface Props {
  counterIS: number | null;
}

interface StateProps {
  shelvesSelected: Shelf[];
  cabinet: Cabinet;
  custom: boolean;
  height: number | null;
  depth: number | null;
  sameDepthIntermediateShelvesInSelection: boolean;
  depthMaxIS: number | null;
  cabinetDepth: number;
  depthValues: number[];
  disallowHeight: number[];
  commonColor: Color | undefined;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  shelvesSelected: makeSelectShelvesSelectedLastCabinet(),
  cabinet: makeSelectLastHistoryCabinet(),
  custom: makeSelectIsActiveCustomMode(),
  height: makeSelectHeightIS(),
  depth: makeSelectDepthIS(),
  sameDepthIntermediateShelvesInSelection: makeSelectIsSameDepthIntermediateShelvesInShelvesSelected(),
  depthMaxIS: makeSelectDepthMaxIS(),
  depthValues: makeSelectDepthValuesIS(),
  cabinetDepth: makeSelectCabinetDepth(),
  disallowHeight: makeSelectAllowISHeight(),
  commonColor: makeSelectCommonColorIS(),
});

const ISPanelContent = ({ counterIS }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isMobile = useMedia({ maxWidth: 1023 });
  const isSmallMobile = useMedia({ maxWidth: 420 });

  const {
    cabinet,
    height,
    depth,
    depthMaxIS,
    depthValues,
    disallowHeight,
    commonColor,
  } = useSelector(stateSelector);

  function onLeave() {
    dispatch(restoreCabinet(cabinet));
  }

  const updateIntermediateShelfHeight = (value: number, preview = false) => {
    dispatch(updateHeightIntermediateShelvesOnSelectionAction(value, preview));
  };

  const updateIntermediateShelfDepth = (value: number, preview = false) => {
    dispatch(updateDepthIntermediateShelvesOnSelectionAction(value, preview));
  };

  let showSliderDepth = false;
  if (
    (isSmallMobile && depthValues.length > 3) ||
    (!isSmallMobile && depthValues.length > 4)
  ) {
    showSliderDepth = true;
  }

  return (
    <div>
      {(counterIS === null || (counterIS && counterIS > 0)) && (
        <div>
          <div className="flex flex-col-reverse lg:flex-row px-2 lg:px-6">
            <div className="lg:mx-2 px-1">
              <div className="flex bg-gray-300">
                <StyledMaterialButton selected={true} onClick={() => {}}>
                  {t('Colors')}
                </StyledMaterialButton>
              </div>
              <div onMouseLeave={onLeave}>
                <ISColorPicker commonColor={commonColor} />
              </div>
            </div>

            <div className="flex flex-col -ml-2 lg:ml-6 lg:mt-6 lg:mt-3">
              {height !== null ? (
                <ISDimensionSelector
                  value={height}
                  valuesList={[125, 175, 225]}
                  handleInput={updateIntermediateShelfHeight}
                  disallowValues={disallowHeight}
                  title={t('Height')}
                  smallTitle={t('short.height')}
                />
              ) : (
                <>
                  {!isMobile && (
                    <div className="max-w-sm bg-gray-200 text-main italic border border-gray-400 py-1 px-2">
                      {t(
                        ' Please, select only one intermediate shelf to modify the height and depth',
                      )}
                    </div>
                  )}
                </>
              )}
              {/*{depthMaxIS === null && (
                <div>
                  {t(
                    'Click on the rack in order to modify the depth of intermediates shelves',
                  )}
                </div>
              )}*/}
              {depth && depthMaxIS !== null && (
                <div className="mt-5">
                  {showSliderDepth ? (
                    <DimensionCabinetSelector
                      value={depth}
                      icon={sizeDepth}
                      handleInput={updateIntermediateShelfDepth}
                      min={marksISDepth[0].value}
                      max={depthMaxIS}
                      marks={marksISDepth.filter(e => e.value <= depthMaxIS)}
                      custom={false}
                      label={t('short.depth')}
                    />
                  ) : (
                    <ISDimensionSelector
                      value={depth}
                      valuesList={depthValues}
                      handleInput={updateIntermediateShelfDepth}
                      disallowValues={depthValues.filter(e => e > depthMaxIS)}
                      title={t('Depth')}
                      smallTitle={t('short.depth')}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ISPanelContent;
